import { useGetUrl } from '@seek/ca-myprofile-utils';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import {
  Stack,
  Box,
  Text,
  TextLink,
  Button,
  Badge,
  Heading,
  Inline,
} from 'braid-design-system';
import type React from 'react';

import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { SHAREABLE_PROFILE } from 'src/components/Router';
import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import { useAppConfig } from 'src/modules/appConfig';
import { hotjarEvent } from 'src/modules/hotjar';

import { ManageUrl } from '../ManageUrl/ManageUrl';
import {
  useCreateShareableProfileUrl,
  useGetShareableProfileStatus,
} from '../data';

import translations from './.vocab';
import { useShareableProfileHotjarEvent } from './useShareableProfileHotjarEvent';

import * as styles from './ShareableProfileStatus.css';

export const ShareableProfileStatus = () => {
  const { t } = useTranslations(translations);
  const { zone, language, brand, isUnifiedSite } = useAppConfig();
  const getUrl = useGetUrl(zone, language, isUnifiedSite);

  const { shareableProfileButtonPressed } = useAnalytics();
  const {
    isShareableProfileActive,
    shareableProfileSlug,
    shareableProfileUrl,
    shareableProfileDisplayUrl,
    shareableProfileViewCount,
    loading,
    error,
  } = useGetShareableProfileStatus();

  const cardName = 'shareable-profile';
  const [
    createShareableProfileUrl,
    {
      loading: createShareableProfileLoading,
      error: createShareableProfileError,
    },
  ] = useCreateShareableProfileUrl({
    key: cardName,
  });

  const onClickHandler = () => {
    shareableProfileButtonPressed({
      eventName: 'profile_update_succeeded',
      isEditState: true,
    });
    createShareableProfileUrl({ zone, languageCode: language });
    hotjarEvent('shareable-profile-created');
    metrics.count('shareable_profile_generated');
  };

  useShareableProfileHotjarEvent();

  const shareableProfileDescription = () => {
    if (shareableProfileSlug === null) {
      return 'Once you create this link, others can view...';
    }
    return isShareableProfileActive
      ? 'Anyone with this link can view and reshare your shareable profile.'
      : 'Anyone with this link can view and reshare your shareable profile, once switched ON.';
  };

  const isAnz = zone === 'anz-1' || zone === 'anz-2';

  const hasNoShareableProfile =
    shareableProfileUrl === null || shareableProfileDisplayUrl === null;

  const showLoading = Boolean(loading || error);

  return (
    <Box
      component="section"
      position="relative"
      id={SHAREABLE_PROFILE}
      data={
        showLoading
          ? {
              automation: `${cardName}-loading-card`,
            }
          : undefined
      }
    >
      <Stack space="large">
        <Stack space="small">
          {!isAnz && <Badge tone="promote">{t('BETA TRIAL')}</Badge>}
          {showLoading ? (
            <LoadingItem type="heading" level="3" width="large" />
          ) : (
            <Heading level="3">
              {t('Share your brand Profile', { brand })}
            </Heading>
          )}
        </Stack>
        {showLoading ? (
          <LoadingItem type="text" size="standard" lines={4} />
        ) : (
          <Text>
            {t(shareableProfileDescription(), {
              TextLink: (children: React.ReactNode) => (
                <TextLink
                  weight="weak"
                  hitArea="large"
                  href={getUrl('shareable-profile-faq-url')}
                >
                  <span className={styles.nowrap}>{children}</span>
                </TextLink>
              ),
            })}
          </Text>
        )}
        {showLoading ? (
          <Stack space="small">
            <LoadingItem type="text" size="small" width="large" />
            <LoadingItem type="button" />
          </Stack>
        ) : (
          <Box>
            {hasNoShareableProfile ? (
              <Inline space="none">
                <Button
                  variant="ghost"
                  tone="neutral"
                  onClick={onClickHandler}
                  loading={
                    createShareableProfileError
                      ? false
                      : createShareableProfileLoading
                  }
                >
                  {t('Consent and create link')}
                </Button>
              </Inline>
            ) : (
              <Stack space="small">
                {shareableProfileViewCount && shareableProfileViewCount > 0 ? (
                  <Text size="small" tone="secondary">
                    {t('Your link has been viewed n times', {
                      shareableProfileViewCount,
                    })}
                  </Text>
                ) : null}
                <ManageUrl
                  mode="READ"
                  profileUrl={shareableProfileUrl}
                  profileDisplayUrl={shareableProfileDisplayUrl}
                  isActive={isShareableProfileActive}
                />
              </Stack>
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );
};
